import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { pick, remove } from 'lodash-es'
import moment from 'moment-timezone'

import { idSelector } from 'selectors/current_user'

import { isSpotAvailable, overlappingEvents } from './availability_service'
import {
  BOOKING_METHODS,
  DATE_FORMAT,
  FETCHED_EVENTS_DAYS_RANGE,
  MAX_SELECTED_POLL_SPOTS,
  maxPollSpotsReachedError,
  maxRangeExceededError
} from './constants'
import { buildEditPollState } from './edit_poll_builder'
import { spotRangeExceedsLimit } from './helpers'
import { colorIndexesSelector, eventTypeIdSelector, fetchedEventsStartRangeSelector } from './selectors'
import {
  calculateDurationInMinutes,
  maybeRemoveInvitees,
  removeConflictingSpots,
  shiftSpotsDuration,
  sortByStartTime
} from './utils'

const initialState = {
  bookingMethod: BOOKING_METHODS.instant,
  editing: false,
  modalVisible: false,
  modalDirty: false,
  modalTransitioned: false,
  currentTab: 0,
  currentTime: moment(),
  loading: false,
  eventTypeId: null,
  detailsStep: {
    fields: {
      name: '',
      details: '',
      location: '',
      locationConfigurations: [],
      phoneNumber: '',
      phoneDetails: '',
      inviteeLocale: '',
      physicalAddress: '',
      physicalDetails: '',
      customLocation: '',
    },
    notifyInvitees: false,
    votesVisible: true
  },
  availabilityStep: {
    dateRangeStart: moment(),
    mobileCurrentView: 'calendar',
    timezone: null,
    rangeStart: null,
    rangeEnd: null,
    fetchInitialEvents: false,
    fetchedEventsStartRange: moment().startOf('week').format(DATE_FORMAT),
    fetchedEventsDaysRange: FETCHED_EVENTS_DAYS_RANGE,
    selectedSpots: [],
    durationField: {
      duration: '30',
      customDurationValue: '',
      customDurationUnit: 'minutes'
    },
    hostsField: {
      isLoading: false,
      suggestions: [],
      colorIndexes: [0, 1, 2, 3, 4, 5, 6, 7, 8],
      selectedMembers: []
    },
    reserveTimes: false,
    errors: [],
    submitDisabled: false,
    selectingSpotsEnabled: true
  },
  submitError: { title: null, body: null },
  isSelectedSpotsDropdownOpen: false
}

const fetchPollData = async (eventTypeId, { dispatch, rejectWithValue }) => {
  const [success, data] = await httpRequest({
    method: 'GET',
    url: `/api/scheduled_events/polls/${eventTypeId}`
  })

  if (!success) {
    dispatch(showHttpError(data))
    return rejectWithValue(data.message)
  }

  return data
}

export const editPoll = createAsyncThunk(
  'oneOffModal/editPoll',
  async ({ eventTypeId }, thunkAPI) => fetchPollData(eventTypeId, thunkAPI)
)

export const duplicatePoll = createAsyncThunk(
  'oneOffModal/duplicatePoll',
  async ({ eventTypeId }, thunkAPI) => fetchPollData(eventTypeId, thunkAPI)
)

const membershipToSuggestion = (membership) => (
  {
    ...pick(membership, 'id', 'user_id', 'user_uuid', 'name', 'email', 'avatar_url',
      'avatar_letter', 'primary_availability_rule_id', 'availability_rules',
      'is_zoom_configured', 'is_gotomeeting_configured', 'google_conferencing_configured',
      'is_microsoft_teams_configured', 'is_webex_configured'),
    holidays: membership.user_holiday_set?.holidays,
    eventsLoadStatus: 'pending'
  }
)

const fetchEvents = (userId, rangeStart, rangeEnd) => httpRequest({
  url: '/api/user_busy_times',
  method: 'GET',
  params: {
    user_uuid: userId,
    start_time: rangeStart,
    end_time: rangeEnd
  }
})

const fetchEventData = async ({ host: { user_uuid: userId }, getState }) => {
  const rangeStart = fetchedEventsStartRangeSelector(getState())
  const rangeEnd = moment(rangeStart).add(FETCHED_EVENTS_DAYS_RANGE, 'days').format(DATE_FORMAT)
  return fetchEvents(userId, rangeStart, rangeEnd)
}

export const addSelectedSuggestion = createAsyncThunk(
  'oneOffModal/addSelectedSuggestion',
  async ({ host }, { rejectWithValue, getState }) => {
    const [success, eventData] = await fetchEventData({ host, getState })
    const colorIndex = colorIndexesSelector(getState())[0]

    const { availability_rules: availabilityRules, ...hostAttrs } = host

    const baseAttrs = {
      ...hostAttrs,
      colorIndex,
      availabilityRules,
      enabled: true
    }

    if (!success) return rejectWithValue(baseAttrs)

    return {
      ...baseAttrs,
      eventData
    }
  }
)

export const updateSelectedSuggestion = createAsyncThunk(
  'oneOffModal/updateSelectedSuggestion',
  async ({ host }, { getState, rejectWithValue }) => {
    const [success, eventData] = await fetchEventData({ host, getState })
    const userId = host.user_id
    const currentUserId = idSelector(getState())
    const eventTypeId = eventTypeIdSelector(getState())

    if (!success) return rejectWithValue(host.user_id)

    if (eventTypeId) {
      remove(eventData, (spot) => spot.kind === 'reserved' && String(spot.event_type_id) === String(eventTypeId))
    }

    return {
      eventData,
      userId,
      hostType: currentUserId === userId ? 'primary' : 'secondary'
    }
  }
)

export const fetchSuggestions = createAsyncThunk(
  'oneOffModal/fetchSuggestions',
  async (query, { dispatch }) => {
    const gettingMemberships = httpRequest({
      url: '/api/organization/memberships',
      method: 'GET',
      params: {
        filter_term: query,
        sort_field: 'name',
        sort_order: 'asc'
      }
    })

    const [successMemberships, responseMemberships] = await gettingMemberships
    if (successMemberships) {
      const suggestions = responseMemberships.results.map(membershipToSuggestion)
      return suggestions
    }
    dispatch(showHttpError(responseMemberships))
    return []
  }
)

const noEventConflicts = (spot, memberEvents) => {
  const { start, end } = spot
  const eventsOverlapping = overlappingEvents(memberEvents, { start, end })

  return isSpotAvailable(eventsOverlapping, true, true)
}

const updatedSelectedSpots = (state, selectedSpots) => {
  const { bookingMethod, availabilityStep: { selectedSpots: currentSpots } } = state
  const orderedSpots = [...currentSpots, ...selectedSpots].sort(sortByStartTime)

  if (!selectedSpots.length || !orderedSpots.length) return state

  if (spotRangeExceedsLimit(orderedSpots)) {
    return {
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        errors: [maxRangeExceededError]
      }
    }
  }

  if (bookingMethod === BOOKING_METHODS.poll && orderedSpots.length > MAX_SELECTED_POLL_SPOTS) {
    return {
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        errors: [maxPollSpotsReachedError]
      }
    }
  }

  return {
    ...state,
    modalDirty: true,
    availabilityStep: {
      ...state.availabilityStep,
      selectedSpots: orderedSpots,
      submitDisabled: false,
      errors: []
    }
  }
}

const filterSelectedSpots = (selectedSpots, memberEvents) => (
  memberEvents.length
    ? selectedSpots.filter((spot) => spot.current || noEventConflicts(spot, memberEvents))
    : selectedSpots
)

const removeSelectedSpots = (state, spotStart, date, timezone) => {
  const { availabilityStep: { selectedSpots } } = state
  const filteredSelectedSpots = date
    ? selectedSpots.filter(({ start }) => !moment(start).tz(timezone).isSame(date, 'day'))
    : selectedSpots.filter(({ start }) => !start.isSame(spotStart))

  return {
    ...state,
    availabilityStep: {
      ...state.availabilityStep,
      selectedSpots: filteredSelectedSpots,
      errors: []
    }
  }
}

const updatedSelectedMember = (state, selectedMember, eventsLoadStatus) => ({
  ...state,
  modalDirty: state.availabilityStep.hostsField.selectedMembers.length > 0,
  availabilityStep: {
    ...state.availabilityStep,
    submitDisabled: false,
    hostsField: {
      ...state.availabilityStep.hostsField,
      colorIndexes: state.availabilityStep.hostsField.colorIndexes.filter((colorIndex) => (
        colorIndex !== selectedMember.colorIndex
      )),
      selectedMembers: Array.from(new Set([
        ...state.availabilityStep.hostsField.selectedMembers.filter(({ user_id: userId }) => userId !== selectedMember.user_id),
        { ...selectedMember, eventsLoadStatus }
      ]))
    },
    errors: []
  }
})

const updateSuggestionEvents = (state, userId, eventData, hostType, eventsLoadStatus) => {
  const memberEvents = eventData.map(({ start_time: start, end_time: end }) => ({ start, end, hostType }))
  const { availabilityStep: { selectedSpots } } = state

  return {
    ...state,
    availabilityStep: {
      ...state.availabilityStep,
      fetchInitialEvents: false,
      selectedSpots: filterSelectedSpots(selectedSpots, memberEvents),
      hostsField: {
        ...state.availabilityStep.hostsField,
        selectedMembers: state.availabilityStep.hostsField.selectedMembers.map((member) => {
          if (member.user_id === userId) {
            return { ...member, eventData, eventsLoadStatus }
          }
          return { ...member }
        })
      }
    }
  }
}

const oneOffModalSlice = createSlice({
  name: 'oneOffModal',
  initialState,
  reducers: {
    openModal: (state, { payload: { bookingMethod } }) => ({
      ...state,
      bookingMethod,
      modalVisible: true,
      modalDirty: false,
      modalTransitioned: false,
    }),
    closeModal: (_state) => initialState,
    transitionModal: (_state) => ({ ...initialState, modalTransitioned: true }),
    setInitialHostAvailability: (state, action) => {
      const initialHostAvailability = action.payload
      const selectedMembers = [...state.availabilityStep.hostsField.selectedMembers]

      return {
        ...state,
        availabilityStep: {
          ...state.availabilityStep,
          hostsField: {
            ...state.availabilityStep.hostsField,
            selectedMembers: selectedMembers.map((m) => {
              if (m.id === initialHostAvailability[0]?.owner_id) {
                return { ...m, availabilityRules: initialHostAvailability }
              }

              return m
            })
          }
        }
      }
    },
    navigateToTab: (state, action) => {
      const { tabNumber } = action.payload
      return { ...state, currentTab: tabNumber }
    },
    setCurrentTime: (state, { payload: currentTime }) => ({
      ...state,
      currentTime
    }),
    updateAvailability: (state, action) => ({
      ...state,
      modalDirty: true,
      availabilityStep: {
        ...state.availabilityStep,
        ...action.payload
      }
    }),
    updateDetailsFields: (state, action) => ({
      ...state,
      modalDirty: true,
      detailsStep: {
        ...state.detailsStep,
        fields: {
          ...state.detailsStep.fields,
          ...action.payload
        }
      }
    }),
    updateAvailabilityStepMobileCurrentView: (state, action) => {
      const { selectedView } = action.payload
      const updatedForm = { ...state.availabilityStep, mobileCurrentView: selectedView }

      return { ...state, availabilityStep: updatedForm }
    },
    resetSubmitError: (state) => ({ ...state, submitError: initialState.submitError }),
    removeMember: (state, action) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        hostsField: {
          ...state.availabilityStep.hostsField,
          colorIndexes: [...state.availabilityStep.hostsField.colorIndexes, action.payload.colorIndex].sort(),
          selectedMembers: [
            ...state.availabilityStep.hostsField.selectedMembers.filter((suggestion) => suggestion.user_id !== action.payload.user_id)
          ]
        }
      }
    }),
    setDatesRange: (state, action) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        rangeStart: action.payload.rangeStart.format(DATE_FORMAT),
        rangeEnd: action.payload.rangeEnd.format(DATE_FORMAT)
      }
    }),
    setFetchedEventsStartRange: (state, action) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        fetchedEventsStartRange: action.payload.rangeStart
      }
    }),
    selectNewSpot: (state, { payload: selectedSpot }) => updatedSelectedSpots(state, [selectedSpot]),
    removeSelectedSpot: (state, { payload: { start } }) => removeSelectedSpots(state, start),
    resetSelectedSpots: (state, { payload: { memberEvents } }) => {
      const { availabilityStep: { selectedSpots } } = state

      return {
        ...state,
        availabilityStep: {
          ...state.availabilityStep,
          selectedSpots: filterSelectedSpots(selectedSpots, memberEvents)
        }
      }
    },
    clearSelectedSpots: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        selectedSpots: []
      }
    }),
    bulkRemoveSelectedSpots: (state, { payload: { date, timezone } }) => removeSelectedSpots(state, null, date, timezone),
    bulkSelectNewSpots: (state, { payload: { spotsForSelection } }) => updatedSelectedSpots(state, spotsForSelection),
    updateDurationField: (state, { payload: { field, value, currentUser } }) => {
      const { editing, bookingMethod } = state
      const { selectingSpotsEnabled, durationField, eventTypeDuration, timezone, hostsField } = state.availabilityStep
      let { selectedSpots } = state.availabilityStep

      const newDurationField = {
        ...state.availabilityStep.durationField,
        [field]: value
      }

      if (selectingSpotsEnabled && durationField[field] !== value) {
        const newDurationInMinutes = calculateDurationInMinutes(newDurationField)
        const potentialSpots = shiftSpotsDuration(selectedSpots, newDurationInMinutes)
        selectedSpots = removeConflictingSpots(
          potentialSpots, timezone, hostsField.selectedMembers, currentUser, true, true
        )

        if (bookingMethod === BOOKING_METHODS.poll && editing) {
          selectedSpots = maybeRemoveInvitees(selectedSpots, eventTypeDuration, newDurationInMinutes)
        }
      }

      return {
        ...state,
        modalDirty: true,
        availabilityStep: {
          ...state.availabilityStep,
          selectedSpots: [...selectedSpots],
          durationField: newDurationField
        }
      }
    },
    toggleReserveTimes: (state) => ({
      ...state,
      modalDirty: true,
      availabilityStep: {
        ...state.availabilityStep,
        reserveTimes: !state.availabilityStep.reserveTimes
      }
    }),
    toggleNotifyInvitees: (state) => ({
      ...state,
      modalDirty: true,
      detailsStep: {
        ...state.detailsStep,
        notifyInvitees: !state.detailsStep.notifyInvitees
      }
    }),
    toggleVotesVisible: (state) => ({
      ...state,
      modalDirty: true,
      detailsStep: {
        ...state.detailsStep,
        votesVisible: !state.detailsStep.votesVisible
      }
    }),
    setAvailabilityStepError: (state, { payload: { error, disabled } }) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        submitDisabled: disabled || state.availabilityStep.submitDisabled,
        errors: [...state.availabilityStep.errors, error]
      }
    }),
    disableSelectingSpots: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        selectingSpotsEnabled: false,
        submitDisabled: true
      }
    }),
    enableSelectingSpots: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        selectingSpotsEnabled: true,
        submitDisabled: !!state.availabilityStep.errors.length || false
      }
    }),
    startLoadingHosts: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        hostsField: { ...state.availabilityStep.hostsField, isLoading: true }
      }
    }),
    startLoadingCalendarData: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        loadingCalendarData: true
      }
    }),
    finishLoadingCalendarData: (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        loadingCalendarData: false
      }
    }),
    setIsSelectedSpotsDropdownOpen: (state, { payload }) => ({
      ...state,
      isSelectedSpotsDropdownOpen: payload
    })
  },
  extraReducers: (builder) => {
    const handlePollPending = (editing = false) => (state) => ({
      ...state,
      bookingMethod: BOOKING_METHODS.poll,
      modalVisible: true,
      ...(editing && { editing: true }),
      loading: true
    })
    const handlePollRejected = (state) => ({ ...state, loading: false })

    builder.addCase(editPoll.pending, handlePollPending(true))
    builder.addCase(editPoll.fulfilled, (state, { payload }) => (
      buildEditPollState(state, payload)
    ))
    builder.addCase(editPoll.rejected, handlePollRejected)
    builder.addCase(duplicatePoll.pending, handlePollPending(false))
    builder.addCase(duplicatePoll.fulfilled, (state, { payload }) => (
      buildEditPollState(state, payload)
    ))
    builder.addCase(duplicatePoll.rejected, handlePollRejected)
    builder.addCase(fetchSuggestions.pending, (state) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        hostsField: { ...state.availabilityStep.hostsField, isLoading: true, suggestions: [] }
      }
    }))
    builder.addCase(fetchSuggestions.fulfilled, (state, action) => ({
      ...state,
      availabilityStep: {
        ...state.availabilityStep,
        hostsField: { ...state.availabilityStep.hostsField, isLoading: false, suggestions: action.payload }
      }
    }))
    builder.addCase(fetchSuggestions.rejected, (state) => ({ ...state }))
    builder.addCase(addSelectedSuggestion.fulfilled, (state, { payload: selectedMember }) => (
      updatedSelectedMember(state, selectedMember, 'done')
    ))
    builder.addCase(addSelectedSuggestion.rejected, (state, { payload: selectedMember }) => (
      updatedSelectedMember(state, selectedMember, 'error')
    ))
    builder.addCase(updateSelectedSuggestion.pending, (state, { meta: { arg: { host } } }) => (
      updateSuggestionEvents(state, host.user_id, [], null, 'pending')
    ))
    builder.addCase(updateSelectedSuggestion.rejected, (state, { payload: userId }) => (
      updateSuggestionEvents(state, userId, [], null, 'error')
    ))
    builder.addCase(updateSelectedSuggestion.fulfilled, (state, {
      payload: { userId, hostType, eventData }
    }) => updateSuggestionEvents(state, userId, eventData, hostType, 'done'))
  }
})

export const {
  reducer,
  actions: {
    openModal,
    navigateToTab,
    closeModal,
    transitionModal,
    setInitialHostAvailability,
    setCurrentTime,
    resetSubmitError,
    removeMember,
    updateDurationField,
    updateAvailability,
    updateDetailsFields,
    updateAvailabilityStepMobileCurrentView,
    setDatesRange,
    setFetchedEventsStartRange,
    selectNewSpot,
    removeSelectedSpot,
    resetSelectedSpots,
    clearSelectedSpots,
    bulkRemoveSelectedSpots,
    bulkSelectNewSpots,
    toggleReserveTimes,
    toggleVotesVisible,
    toggleNotifyInvitees,
    setAvailabilityStepError,
    enableSelectingSpots,
    disableSelectingSpots,
    startLoadingHosts,
    startLoadingCalendarData,
    finishLoadingCalendarData,
    setIsSelectedSpotsDropdownOpen
  }
} = oneOffModalSlice
